/* eslint-disable */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  IconButton,
  Box,
  Typography,
  Chip,
  FormHelperText,
  CircularProgress,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import { fetcher, postFetcher } from 'src/utils/axios';
import { enqueueSnackbar } from 'notistack';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { useInventory } from 'src/context/inventory-create-provider';

// Color tokens matching the "Create Node Template" screenshot
const C = {
  bg: '#F5F5F5',
  surface: '#FFFFFF',
  border: '#E0E0E0',
  divider: '#F0F0F0',
  navy: '#1A1A2E',
  navyHover: '#2D2D4E',
  textPrimary: '#1A1A2E',
  textSecondary: '#6B7280',
  textMuted: '#9CA3AF',
  tableHead: '#F9FAFB',
  errorRed: '#DC2626',
  successGreen: '#16A34A',
  warningAmber: '#D97706',
  disabledBg: '#D1D5DB',
  disabledText: '#9CA3AF',
};

const dialogPaper = {
  borderRadius: '12px',
  backgroundColor: C.bg,
  border: `1px solid ${C.border}`,
  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
};

const titleSx = {
  backgroundColor: C.surface,
  borderBottom: `1px solid ${C.border}`,
  px: 3,
  py: 2,
};

const actionsSx = {
  backgroundColor: C.surface,
  borderTop: `1px solid ${C.border}`,
  px: 3,
  py: 2,
  gap: 1,
};

const cancelBtnSx = {
  color: '#374151',
  borderColor: C.border,
  borderRadius: '8px',
  minWidth: 100,
  textTransform: 'none',
  fontWeight: 500,
  '&:hover': { borderColor: '#9CA3AF', backgroundColor: '#F9FAFB' },
  '&:disabled': { borderColor: C.disabledBg, color: C.disabledText },
};

const primaryBtnSx = {
  backgroundColor: C.navy,
  color: '#FFFFFF',
  borderRadius: '8px',
  minWidth: 120,
  textTransform: 'none',
  fontWeight: 500,
  '&:hover': { backgroundColor: C.navyHover },
  '&:disabled': { backgroundColor: C.disabledBg, color: C.disabledText },
};

const tableHeadCellSx = {
  backgroundColor: C.tableHead,
  color: C.textMuted,
  fontSize: '0.68rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  fontWeight: 600,
  borderBottom: `1px solid ${C.border}`,
};

const tableCellSx = {
  borderBottom: `1px solid ${C.divider}`,
  color: C.textPrimary,
  fontSize: '0.85rem',
  py: 1.25,
};

const inputSx = (focused) => ({
  '& .MuiInputBase-input': { fontSize: '0.82rem', py: 0.75 },
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    backgroundColor: C.surface,
    '& fieldset': { borderColor: C.border },
    '&:hover fieldset': { borderColor: '#9CA3AF' },
    '&.Mui-focused fieldset': { borderColor: C.navy },
  },
});

const selectSx = {
  fontSize: '0.82rem',
  borderRadius: '6px',
  backgroundColor: C.surface,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9CA3AF' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: C.navy },
};



/* ------------------------------------------------------------------ */
/* PREVIEW DIALOG                                                      */
/* ------------------------------------------------------------------ */
function PreviewDialog({ open, onClose, PODialogClose, products, vendors, notes, jobId, nodeids,jobpayload,updatejobpayload }) {

  const [poNumbers, setPoNumbers] = useState([]);
  const router = useRouter();

  const groupedByVendor = products.reduce((acc, product) => {
    const vendorId = product.vendor;
    if (!acc[vendorId]) {
      acc[vendorId] = {
        vendor: vendors.find((v) => v.id === vendorId),
        products: [],
        totalQty: 0,
      };
    }
    acc[vendorId].products.push(product);
    acc[vendorId].totalQty += product.qtyToOrder;
    return acc;
  }, {});

  const vendorGroups = Object.values(groupedByVendor);
  const grandTotal = vendorGroups.reduce((sum, g) => sum + g.totalQty, 0);

  const handleSubmit = async () => {
    const payload = { job_id: jobId, products, notes, nodeids,jobpayload };
    try {
      const res = await postFetcher(['purchaseorders-app/create/po/', payload]);
      console.log('PO Creation Response:', res);
      updatejobpayload([])
      enqueueSnackbar(res.message || 'Purchase Order created successfully', { variant: 'success' });
      onClose();
      PODialogClose();
      router.push(paths.dashboard.kanban);
    } catch (e) {
      console.error('Failed to create PO:', e);
      enqueueSnackbar('Failed to create Purchase Order', { variant: 'error' });
    }
  };

  const uniqueVendorCount = React.useMemo(() => new Set(products.map((p) => p.vendor)).size, [products]);
  const previousVendorCountRef = React.useRef(0);

  useEffect(() => {
    if (!open || uniqueVendorCount === 0 || uniqueVendorCount === previousVendorCountRef.current) {
      return;
    }

    previousVendorCountRef.current = uniqueVendorCount;
    let active = true;

    const fetchPoNumbers = async () => {
      try {
        const endpoint = `purchaseorders-app/generate-po-number/?count=${uniqueVendorCount}`;
        const res = await fetcher([endpoint]);
        if (active) {
          setPoNumbers(res.po_numbers || []);
        }
      } catch (e) {
        console.error('Failed to fetch PO numbers:', e);
      }
    };

    fetchPoNumbers();

    return () => {
      active = false;
    };
  }, [open, uniqueVendorCount]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: dialogPaper }}>
      <DialogTitle sx={titleSx}>
        <Typography variant="h6" fontWeight={600} fontSize="1rem" color={C.textPrimary} letterSpacing="-0.01em">
          Purchase Order Preview
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: C.bg, borderColor: C.border, px: 3, py: 2.5 }}>
        {vendorGroups.length === 0 ? (
          <Box
            sx={{
              py: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1.5px dashed ${C.border}`,
              borderRadius: '8px',
              backgroundColor: C.surface,
            }}
          >
            <Typography fontSize="0.9rem" color={C.textMuted}>
              No products to order.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {vendorGroups.map((group, index) => (
              <Paper
                key={group.vendor.id}
                variant="outlined"
                sx={{ p: 2.5, backgroundColor: C.surface, borderColor: C.border, borderRadius: '10px' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 2,
                    pb: 1.5,
                    borderBottom: `1px solid ${C.divider}`,
                  }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: C.navy, flexShrink: 0 }} />
                  <Typography variant="subtitle1" fontWeight={600} color={C.textPrimary} fontSize="0.9rem">
                    {poNumbers[index] ? `${poNumbers[index]} – ` : ''}
                    {group.vendor.vendor_name}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr 80px', gap: 1, px: 1, mb: 0.5 }}>
                    {['SKU', 'Product', 'Qty'].map((h) => (
                      <Typography
                        key={h}
                        sx={{
                          fontSize: '0.65rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          color: C.textMuted,
                          fontWeight: 600,
                        }}
                      >
                        {h}
                      </Typography>
                    ))}
                  </Box>

                  {group.products.map((product) => (
                    <Box
                      key={product.id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '120px 1fr 80px',
                        gap: 1,
                        alignItems: 'center',
                        py: 0.75,
                        px: 1,
                        borderBottom: `1px solid ${C.divider}`,
                        '&:last-child': { borderBottom: 'none' },
                      }}
                    >
                      <Typography variant="body2" fontWeight={600} color={C.textSecondary} fontSize="0.8rem">
                        {product.sku}
                      </Typography>
                      <Typography variant="body2" color={C.textPrimary} fontSize="0.85rem">
                        {product.name}
                      </Typography>
                      <Typography variant="body2" fontWeight={700} color={C.navy} fontSize="0.85rem">
                        {product.qtyToOrder}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box
                  sx={{
                    mt: 1.5,
                    pt: 1.5,
                    borderTop: `1px solid ${C.divider}`,
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Typography variant="body2" color={C.textSecondary} fontSize="0.8rem">
                    Sub-total:{' '}
                    <Typography component="span" fontWeight={700} color={C.textPrimary}>
                      {group.totalQty} units
                    </Typography>
                  </Typography>
                </Box>
              </Paper>
            ))}

            <Box
              sx={{
                p: 2,
               
                borderRadius: '10px',
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}  fontSize="0.9rem">
                Grand Total: {grandTotal} units
              </Typography>
            </Box>

            {notes && (
              <Paper
                variant="outlined"
                sx={{ p: 2, backgroundColor: C.surface, borderColor: C.border, borderRadius: '10px' }}
              >
                <Typography
                  sx={{
                    fontSize: '0.68rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: C.textMuted,
                    fontWeight: 600,
                    mb: 0.75,
                  }}
                >
                  Notes
                </Typography>
                <Typography variant="body2" color={C.textPrimary} fontSize="0.85rem">
                  {notes}
                </Typography>
              </Paper>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={actionsSx}>
        <Button onClick={onClose} variant="outlined" sx={cancelBtnSx}>
          Close
        </Button>
        <Button onClick={handleSubmit} variant="contained" sx={primaryBtnSx}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* MAIN PO DIALOG                                                      */
/* ------------------------------------------------------------------ */
export default function PODialog({
  open,
  onClose,
  requiredQuantity,
  availableQuantity,
  productInfo={} ,
  shortageReport = [],
  initialVendorId = '',
  jobId,
  nodeids,
}) {
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [defaultVendor, setDefaultVendor] = useState('');
  const [vendors, setVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [notes, setNotes] = useState('');
  const initializedRef = React.useRef(false);
  const {jobpayload,updatejobpayload} = useInventory();
  const loadVendors = async () => {
    setLoadingVendors(true);
    try {
      const res = await fetcher(['products-app/vendor/dropdown/']);
      const data = res || [];
      setVendors(data);
      if (data.length > 0) {
        setDefaultVendor(initialVendorId || data[0].id); 
      }
    } catch (e) {
      console.error('Failed to load vendors:', e);
    } finally {
      setLoadingVendors(false);
    }
  };

  
  useEffect(() => {
    loadVendors();
  }, []);

  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      setSelectedProducts([]);
      return;
    }

    if (initializedRef.current || vendors.length === 0) {
      return;
    }

    initializedRef.current = true;

    if (shortageReport && shortageReport.length > 0) {
      const initialProducts = shortageReport.map((item) => {
        const variantAttrs = Object.entries(item.variant.attributes)
          .filter(([, v]) => v)
          .map(([k, v]) => `${k}: ${v}`)
          .join(', ');
        const displayName = variantAttrs
          ? `${item.product.name} (${variantAttrs})`
          : item.product.name;
        return {
          id: `${item.product.id}-${item.variant.id}`,
          variant_id: item.variant.id,
          product_id: item.product.id,
          sku: item.product.sku,
          name: displayName,
          availableQty: item.available_qty,
          requiredQty: item.required_qty,
          qtyToOrder: item.shortage_qty,
          vendor: defaultVendor || (vendors.length > 0 ? vendors[0].id : ''),
          unit: item.unit,
        };
      });
      setSelectedProducts(initialProducts);
    } else if (requiredQuantity !== undefined && availableQuantity !== undefined) {
      const initialProduct = {
        ...productInfo,
        availableQty: availableQuantity,
        requiredQty: requiredQuantity,
        qtyToOrder: Math.max(requiredQuantity - availableQuantity, 0),
        vendor: defaultVendor || (vendors.length > 0 ? vendors[0].id : ''),
      };
      setSelectedProducts([initialProduct]);
    } else {
      setSelectedProducts([]);
    }
   }, [open, shortageReport, requiredQuantity, availableQuantity, defaultVendor, vendors]);


  const handleRemoveProduct = (id) => setSelectedProducts((prev) => prev.filter((p) => p.id !== id));

  const handleQtyChange = (id, value) => {
    const qty = Math.max(0, parseFloat(value) || 0);
    setSelectedProducts((prev) => prev.map((p) => (p.id === id ? { ...p, qtyToOrder: qty } : p)));
  };

  const handleRequiredQtyChange = (id, value) => {
    const req = Math.max(0, parseFloat(value) || 0);
    setSelectedProducts((prev) =>
      prev.map((p) => {
        if (p.id === id) {
          const suggested = Math.max(req - p.availableQty, 0);
          return { ...p, requiredQty: req, qtyToOrder: suggested };
        }
        return p;
      })
    );
  };

  const handleVendorChange = (id, vendorId) => {
    setSelectedProducts((prev) => prev.map((p) => (p.id === id ? { ...p, vendor: vendorId } : p)));
  };

  const handlePreview = () => setPreviewOpen(true);

  const totalOrderQty = selectedProducts.reduce((sum, p) => sum + p.qtyToOrder, 0);
  const hasValidOrder = totalOrderQty > 0;
  const isVendorsLoading = loadingVendors || vendors.length === 0;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: dialogPaper }}>
        <DialogTitle sx={titleSx}>
          <Typography variant="h6" fontWeight={600} fontSize="1rem" color={C.textPrimary} letterSpacing="-0.01em">
            Create Purchase Order
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ backgroundColor: C.bg, px: 3, pt: 2.5, pb: 2 }}>
          {/* Default vendor selector */}
          <Box sx={{ mb: 2.5, mt: 0.5, display: 'flex', gap: 2 }}>
            <Box sx={{ minWidth: 220, position: 'relative',p:1 }}>
              <FormControl fullWidth size="medium">
                <InputLabel
                  sx={{
                    fontSize: '0.85rem',
                    color: C.textSecondary,
                    '&.Mui-focused': { color: C.navy },
                  }}
                >
                  Default Vendor
                </InputLabel>
                <Select
                  value={defaultVendor}
                  onChange={(e) => setDefaultVendor(e.target.value)}
                  label="Default Vendor"
                  disabled={isVendorsLoading}
                  sx={{
                    backgroundColor: C.surface,
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9CA3AF' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: C.navy },
                  }}
                >
                  <MenuItem value="">
                    <em style={{ color: C.textMuted, fontSize: '0.85rem' }}>None</em>
                  </MenuItem>
                  {vendors.map((vendor) => (
                    <MenuItem key={vendor.id} value={vendor.id} sx={{ fontSize: '0.85rem' }}>
                      {vendor.vendor_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {loadingVendors && (
                <CircularProgress
                  size={16}
                  sx={{ position: 'absolute', right: 36, top: '50%', mt: '-8px', color: C.navy }}
                />
              )}
            </Box>
          </Box>

          {/* Products table */}
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: 400,
              borderRadius: '10px',
              border: `1px solid ${C.border}`,
              boxShadow: 'none',
              backgroundColor: C.surface,
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[
                    { label: 'Product SKU', align: 'left' },
                    { label: 'Name', align: 'left' },
                    { label: 'Available Qty', align: 'right' },
                    { label: 'Required Qty', align: 'right' },
                    { label: 'Qty to Order', align: 'right' },
                    { label: 'Vendor', align: 'left' },
                    { label: 'Action', align: 'center' },
                  ].map(({ label, align }) => (
                    <TableCell key={label} align={align} sx={tableHeadCellSx}>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {isVendorsLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ borderBottom: 'none' }}>
                      <Box sx={{ py: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                        <CircularProgress size={28} sx={{ color: C.navy }} />
                        <Typography fontSize="0.82rem" color={C.textMuted}>
                          Loading vendors…
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : selectedProducts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ borderBottom: 'none' }}>
                      <Box
                        sx={{
                          py: 5,
                          border: `1.5px dashed ${C.border}`,
                          borderRadius: '8px',
                          mx: 2,
                          my: 1,
                        }}
                      >
                        <Typography fontSize="0.82rem" color={C.textMuted}>
                          No products added. Use the search above to add products.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  selectedProducts.map((product) => {
                    const suggestedQty = Math.max(product.requiredQty - product.availableQty, 0);
                    const showHelper = product.qtyToOrder === 0 && suggestedQty > 0;

                    return (
                      <TableRow key={product.id} sx={{ '&:hover': { backgroundColor: '#FAFAFA' } }}>
                        <TableCell sx={{ ...tableCellSx, fontWeight: 600, color: C.textSecondary, fontSize: '0.78rem' }}>
                          {product.sku}
                        </TableCell>
                        <TableCell sx={{ ...tableCellSx, maxWidth: 200 }}>
                          <Typography fontSize="0.83rem" color={C.textPrimary} noWrap>
                            {product.name}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={tableCellSx}>
                          <Typography fontSize="0.83rem" fontWeight={600} color={C.successGreen}>
                            {product.availableQty}
                          </Typography>
                        </TableCell>

                        <TableCell align="right" sx={tableCellSx}>
                          <TextField
                            type="number"
                            size="small"
                            value={product.requiredQty}
                            onChange={(e) => handleRequiredQtyChange(product.id, e.target.value)}
                            inputProps={{ min: 0, step: '0.1' }}
                            disabled
                            sx={{
                              width: 90,
                              '& .MuiInputBase-input': { fontSize: '0.82rem', textAlign: 'right', py: 0.75 },
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '6px',
                                backgroundColor: C.bg,
                                '& fieldset': { borderColor: C.border },
                              },
                            }}
                          />
                        </TableCell>

                        <TableCell align="right" sx={tableCellSx}>
                          <Box>
                            <TextField
                              type="number"
                              size="small"
                              value={product.qtyToOrder}
                              onChange={(e) => handleQtyChange(product.id, e.target.value)}
                              inputProps={{ min: 0, step: '0.1' }}
                              disabled={isVendorsLoading}
                              sx={{
                                width: 90,
                                '& .MuiInputBase-input': { fontSize: '0.82rem', textAlign: 'right', py: 0.75 },
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '6px',
                                  backgroundColor: C.surface,
                                  '& fieldset': { borderColor: C.border },
                                  '&:hover fieldset': { borderColor: '#9CA3AF' },
                                  '&.Mui-focused fieldset': { borderColor: C.navy },
                                },
                              }}
                            />
                            {showHelper && (
                              <FormHelperText sx={{ mt: 0.5, fontSize: '0.68rem', color: C.warningAmber }}>
                                Suggested: {suggestedQty}
                              </FormHelperText>
                            )}
                          </Box>
                        </TableCell>

                        <TableCell sx={{ ...tableCellSx, minWidth: 140 }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={product.vendor}
                              onChange={(e) => handleVendorChange(product.id, e.target.value)}
                              disabled={isVendorsLoading}
                              sx={selectSx}
                            >
                              {vendors.map((vendor) => (
                                <MenuItem key={vendor.id} value={vendor.id} sx={{ fontSize: '0.82rem' }}>
                                  {vendor.vendor_name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>

                        <TableCell align="center" sx={tableCellSx}>
                          {selectedProducts.length > 1 && (
                            <IconButton
                              onClick={() => handleRemoveProduct(product.id)}
                              size="small"
                              disabled={isVendorsLoading}
                              sx={{
                                color: C.errorRed,
                                opacity: 0.7,
                                borderRadius: '6px',
                                '&:hover': { opacity: 1, backgroundColor: '#FEE2E2' },
                              }}
                            >
                              <Iconify icon="material-symbols:delete" width={16} />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Notes */}
          <TextField
            margin="normal"
            label="Notes (Optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={3}
            fullWidth
            disabled={isVendorsLoading}
            sx={{
              mt: 2,
              '& .MuiInputBase-root': { backgroundColor: C.surface, borderRadius: '8px', fontSize: '0.85rem' },
              '& .MuiInputLabel-root': { fontSize: '0.85rem', color: C.textSecondary },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: C.border },
                '&:hover fieldset': { borderColor: '#9CA3AF' },
                '&.Mui-focused fieldset': { borderColor: C.navy },
              },
              '& .MuiInputLabel-root.Mui-focused': { color: C.navy },
            }}
          />
        </DialogContent>

        <DialogActions sx={actionsSx}>
          <Button onClick={onClose} variant="outlined" disabled={loadingVendors} sx={cancelBtnSx}>
            Cancel
          </Button>
          <Button
            onClick={handlePreview}
            variant="contained"
            disabled={!hasValidOrder || isVendorsLoading}
            sx={primaryBtnSx}
          >
            Preview
          </Button>
        </DialogActions>
      </Dialog>

      <PreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        PODialogClose={onClose}
        products={selectedProducts}
        vendors={vendors}
        notes={notes}
        jobId={jobId}
        nodeids={nodeids}
        jobpayload={jobpayload}
        updatejobpayload={updatejobpayload}
      />
    </>
  );
}